import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import { Button, Loading, MultiSelect } from '@commenda/kakashi';
import { TCurrencyInfo, TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getPrintableDirectorName } from '@commenda/utils/get-printable-director-name';
import { Currency, StakeholderType } from '@prisma/client';
import { CurrencyHelper } from '@commenda/utils/currency';
import { useEffect, useState } from 'react';
import { KeyedMutator } from 'swr';
import { IncorporationFormHeading } from '../common';
import CompanyValuationInput from '../common/CompanyValuationInput';
import CorporateStakeholderModal from '../common/corporateStakeholder/CorporateStakeholderModal';
import { CorporateShareholderEquitySplitDistribution } from '../common/corporateStakeholder/EquitySplitDistribution';
import StakeholderOptionsModal from '../common/corporateStakeholder/StakeholderOptionsModal';
import DirectorModal from '../Director/new/DirectorModal';
import CapTableKS from '../EquityInfo/CapTable';
import { useEquityFormState } from '../EquityInfo/hooks/useEquityFormState';
import { useEquityResources } from '../EquityInfo/hooks/useEquityResources';
import DirectorForm from './DirectorForm';
import { validateSingaporeanStakeholders } from './validators';

const EquityInfo = ({
  next,
  previous,
  data,
  mutate,
  setNotification,
  token,
  companyId,
  corporationId,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
  token: string;
  companyId: number;
  corporationId: number;
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState<TCurrencyInfo>();

  const {
    directorsData,
    corporations,
    capTableTransactions,
    stockClasses,
    mutateDirectors,
    mutateCorporations,
    mutateCapTableTransactions,
    stakeholderOptions,
    isLoading,
    isError,
  } = useEquityResources(companyId, corporationId);

  const {
    equityInfo,
    setEquityInfo,
    selectedStakeholders,
    setSelectedStakeholders,
    setCorporateShareholderToEdit,
    corporateStakeholderModalVisibility,
    directorModalVisibility,
    setIndividualShareholderToEdit,
    stakeholderIdsWithError,
    setStakeholderIdsWithError,
    setCapTableTransactionIdsToDelete,
    isSubmitting,
    stakeholderTypeModalVisibility,
    onAddCorporateShareholder,
    onSubmit,
    selectAddedShareholder,
    onChangeShareholder,
    individualShareholderToEdit,
    corporateShareholderToEdit,
    capTableData,
    stockClass,
    onEditShareholderDetails,
  } = useEquityFormState({
    capTableTransactions,
    stockClasses,
    directorsData: directorsData?.directors,
    corporations,
    mutateCorporations,
    resetEditModalPresetState: () => {},
    corporationId,
    config: {
      defaultShareCount: 1,
      defaultSharePrice: 1,
      currency: selectedCurrency?.currency,
    },
    onSubmitSuccess: () => {
      mutate();
      mutateCapTableTransactions();
      next?.();
    },
    validateStakeholdersAndGetIdsWithError: validateSingaporeanStakeholders,
  });

  useEffect(() => {
    if (!stockClass) {
      setSelectedCurrency(CurrencyHelper.getCurrencyInfo(Currency.SGD));
      return;
    }

    setSelectedCurrency(
      CurrencyHelper.getCurrencyInfo(
        stockClass.additionalInformation.pricePerShare.currency
      )
    );
  }, [stockClass]);

  if (isLoading || isError) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-10 max-w-[700px]">
      <IncorporationFormHeading
        text="Allocate company ownership"
        additionalText="Please provide information about this company’s members."
      />

      <div className="">
        <CompanyValuationInput
          equityInfo={equityInfo}
          setEquityInfo={setEquityInfo}
          shareCountPlaceholder="1"
          sharePricePlaceholder="1"
          helpTexts={{
            mainHeading: 'Paid Up Capital',
            bodyText:
              'We suggest you to keep the following default values to reduce your tax burden.',
          }}
          currency={selectedCurrency}
          setSelectedCurrency={setSelectedCurrency}
        />
      </div>
      <div>
        <div className="flex gap-1">
          <h3 className="text-base">
            Shareholders Equity
            <span className="ml-0.5 text-utility-violet-600">*</span>
          </h3>
        </div>
        <span className="text-sm text-neutral-2">
          Each share is worth{' '}
          <span className="font-bold text-neutral-700">
            {selectedCurrency?.style} {equityInfo.price}
          </span>{' '}
          of paid-up capital. You can easily add more shares or invest more in
          your company later on.
        </span>
      </div>
      <div className=" flex flex-col gap-4 pb-10 border-b -mt-5">
        <MultiSelect
          defaultValue={Object.keys(selectedStakeholders || {})}
          id="shareholder-multiselect"
          placeholder="Select shareholders"
          options={
            stakeholderOptions
              ?.map((stakeholder) => {
                return {
                  label: stakeholder.name,
                  value: stakeholder.id,
                };
              })
              ?.sort((a, b) => a.label.localeCompare(b.label)) || []
          }
          onValueChange={onChangeShareholder}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add a shareholder',
            onAddItem: stakeholderTypeModalVisibility.show,
          }}
        />
        <StakeholderOptionsModal
          isShown={stakeholderTypeModalVisibility.visible}
          toggle={stakeholderTypeModalVisibility.hide}
          onShowDirectorModal={directorModalVisibility.show}
          onShowCorporateShareholderModal={() => {
            stakeholderTypeModalVisibility.hide();
            corporateStakeholderModalVisibility.show();
          }}
        />
        <CorporateShareholderEquitySplitDistribution
          selectedStakeholders={selectedStakeholders}
          setSelectedStakeholders={setSelectedStakeholders}
          equityInfo={equityInfo}
          onChangeShareholder={onChangeShareholder}
          onEditShareholderDetails={onEditShareholderDetails}
          stakeholderIdsWithError={Array.from(stakeholderIdsWithError)}
          setNotification={setNotification}
          setCapTableTransactionIdsToDelete={setCapTableTransactionIdsToDelete}
        />
      </div>

      <CapTableKS data={capTableData} />

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button onClick={onSubmit} disabled={isSubmitting}>
          <span>Continue</span>
          <ProceedIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>
      <DirectorModal
        isOpen={directorModalVisibility.visible}
        director={individualShareholderToEdit}
        setOpen={directorModalVisibility.hide}
        companyDataMutate={mutateDirectors}
        DirectorForm={DirectorForm}
        setNotification={setNotification}
        companyId={companyId}
        corporationId={corporationId}
        token={token}
        workRequestData={data}
        modalConfig={{
          heading: individualShareholderToEdit
            ? 'Edit individual shareholder'
            : 'Add individual shareholder',
          postSave: (dir) => {
            stakeholderTypeModalVisibility.hide();
            selectAddedShareholder({
              type: StakeholderType.INDIVIDUAL,
              id: dir.id,
              name: getPrintableDirectorName(dir),
            });
            setCorporateShareholderToEdit(undefined);
            setStakeholderIdsWithError((prev) => {
              const newSet = new Set(prev);
              newSet.delete(`INDIVIDUAL_${dir.id}`);
              return newSet;
            });
          },
        }}
      />

      <CorporateStakeholderModal
        selectedCorporateStakeholderId={corporateShareholderToEdit}
        setSelectedCorporateStakeholderId={setCorporateShareholderToEdit}
        companyId={companyId}
        isShown={corporateStakeholderModalVisibility.visible}
        toggle={corporateStakeholderModalVisibility.hide}
        postSave={({
          corporationId: corporateStakeholderId,
          corporationName,
        }) =>
          onAddCorporateShareholder({
            corporateStakeholderId,
            corporationName,
          })
        }
        mutate={async () => {
          await mutateCorporations();
        }}
        setNotification={setNotification}
        headerTextOverride={
          corporateShareholderToEdit
            ? 'Edit corporate shareholder'
            : 'Add corporate shareholder'
        }
        DirectorForm={DirectorForm}
        directors={directorsData?.directors || []}
        mutateDirectors={mutateDirectors}
        corporations={corporations}
        workRequestData={data}
      />
    </div>
  );
};

export default EquityInfo;
